import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher/index.ts'
import { useWallet } from '@/hooks/useWallet.ts'
import { useAdmin } from '@/hooks/useAdmin.ts'
import { cn } from '@/utils/cn.ts'

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const { isConnected, disconnect } = useWallet()
  const { isOwner } = useAdmin()
  const [menuOpen, setMenuOpen] = useState(false)

  const isHome = location.pathname === '/'

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), [])
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  useEffect(() => {
    const id = setTimeout(() => closeMenu(), 0)
    return () => clearTimeout(id)
  }, [location.pathname, closeMenu])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    el?.scrollIntoView({ behavior: 'smooth' })
    closeMenu()
  }

  const isActivePath = (path: string) => location.pathname === path

  const navLinkBase =
    'cursor-pointer bg-transparent p-0 text-[0.875rem] font-medium whitespace-nowrap text-fg-secondary transition-colors duration-fast hover:text-fg max-md:w-full max-md:py-2 max-md:text-base'

  const disconnectButtonClass = cn(
    'cursor-pointer rounded-md border bg-transparent px-3 py-1.5 text-[0.875rem] font-medium text-fg-secondary transition-all',
    'border-line hover:border-error hover:text-error',
    'max-md:w-full max-md:text-base max-md:py-2',
  )

  return (
    <header className="z-sticky h-header border-line sticky top-0 border-b bg-[rgba(10,1,24,0.93)] backdrop-blur-[24px]">
      <div className="max-w-content mx-auto flex h-full items-center justify-between px-4 md:px-8">
        <Link
          to="/"
          className="z-10 flex items-center gap-2"
          onClick={closeMenu}
        >
          <img
            src="/bitslegacy_logo.png"
            alt="BitsLegacy"
            className="h-14 w-14 object-contain max-md:h-14 max-md:w-14 md:h-20 md:w-20"
          />
          <span className="text-fg text-[1.0625rem] font-bold tracking-tight md:text-xl">
            {t('app.name')}
          </span>
        </Link>

        <button
          type="button"
          className="z-10 hidden cursor-pointer flex-col justify-center gap-[5px] border-none bg-transparent p-1 max-md:flex"
          onClick={toggleMenu}
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
        >
          <span
            className={cn(
              'bg-fg duration-base block h-0.5 w-[22px] origin-center rounded-sm transition-all',
              menuOpen && 'translate-y-[7px] rotate-45',
            )}
          />
          <span
            className={cn(
              'bg-fg duration-base block h-0.5 w-[22px] origin-center rounded-sm transition-all',
              menuOpen && 'opacity-0',
            )}
          />
          <span
            className={cn(
              'bg-fg duration-base block h-0.5 w-[22px] origin-center rounded-sm transition-all',
              menuOpen && '-translate-y-[7px] -rotate-45',
            )}
          />
        </button>

        <nav
          className={cn(
            'max-md:border-line max-md:bg-surface-raised max-md:duration-slow flex items-center gap-6 max-md:fixed max-md:top-0 max-md:right-0 max-md:z-[5] max-md:h-dvh max-md:w-[280px] max-md:flex-col max-md:items-start max-md:gap-4 max-md:overflow-y-auto max-md:border-l max-md:px-8 max-md:pt-[calc(var(--header-height)+1.5rem)] max-md:pb-8 max-md:transition-transform',
            menuOpen ? 'max-md:translate-x-0' : 'max-md:translate-x-full',
          )}
        >
          <LanguageSwitcher />

          {isHome ? (
            <button
              type="button"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
                closeMenu()
              }}
              className={navLinkBase}
            >
              {t('nav.home')}
            </button>
          ) : (
            <Link to="/" className={navLinkBase} onClick={closeMenu}>
              {t('nav.home')}
            </Link>
          )}

          {isHome ? (
            <button
              type="button"
              onClick={() => scrollTo('features')}
              className={navLinkBase}
            >
              {t('nav.features')}
            </button>
          ) : (
            <Link to="/#features" className={navLinkBase} onClick={closeMenu}>
              {t('nav.features')}
            </Link>
          )}

          {isHome ? (
            <button
              type="button"
              onClick={() => scrollTo('pricing')}
              className={navLinkBase}
            >
              {t('nav.pricing')}
            </button>
          ) : (
            <Link to="/#pricing" className={navLinkBase} onClick={closeMenu}>
              {t('nav.pricing')}
            </Link>
          )}

          {isConnected && (
            <>
              <Link
                to="/dashboard"
                className={cn(
                  navLinkBase,
                  isActivePath('/dashboard') && 'text-accent',
                )}
                onClick={closeMenu}
              >
                {t('nav.dashboard')}
              </Link>
              <Link
                to="/profile"
                className={cn(
                  navLinkBase,
                  isActivePath('/profile') && 'text-accent',
                )}
                onClick={closeMenu}
              >
                {t('nav.profile')}
              </Link>
              <Link
                to="/dashboard/activity"
                className={cn(
                  navLinkBase,
                  isActivePath('/dashboard/activity') && 'text-accent',
                )}
                onClick={closeMenu}
              >
                {t('nav.activity')}
              </Link>
              {isOwner && (
                <Link
                  to="/admlgn"
                  className={cn(
                    navLinkBase,
                    'text-warning hover:opacity-85',
                    isActivePath('/admlgn') && 'text-accent',
                  )}
                  onClick={closeMenu}
                >
                  {t('nav.admin')}
                </Link>
              )}
            </>
          )}

          {isConnected && (
            <button
              type="button"
              onClick={async () => {
                await disconnect()
                closeMenu()
              }}
              className={disconnectButtonClass}
            >
              {t('dashboard.disconnect')}
            </button>
          )}
        </nav>
      </div>

      {menuOpen && (
        <div
          className="fixed inset-0 z-[4] hidden bg-black/50 max-md:block"
          onClick={closeMenu}
          onKeyDown={closeMenu}
          role="presentation"
        />
      )}
    </header>
  )
}
